import * as scope from '../scope'
import type { Statement } from '../statement'
import type { Urn } from '../urn'

import type { Role } from './role'

/**
 * A global role defined in code and seeded into storage, so its definition
 * cannot drift from the action registry. Each replaces one legacy access flag
 * at one level
 */
export interface BuiltInRole {
  readonly id: Role.Id
  readonly name: string
  readonly description: string
  readonly statements: readonly Statement.Templated[]
}
export namespace BuiltInRole {
  export const Id = {
    TenantViewer: 'builtin-tenant-viewer',
    TenantUserManager: 'builtin-tenant-user-manager',
    TenantEditor: 'builtin-tenant-editor',
    TenantCreator: 'builtin-tenant-creator',
    TenantDeleter: 'builtin-tenant-deleter',
    ApiKeyIssuerInTenant: 'builtin-api-key-issuer-tenant',
    ApiKeyIssuerInZone: 'builtin-api-key-issuer-zone',
    ScenarioCreator: 'builtin-scenario-creator',
    ScenarioDeleter: 'builtin-scenario-deleter',
    AppConfigurator: 'builtin-app-configurator',
    AgentConfiguratorInTenant: 'builtin-agent-configurator-tenant',
    AgentConfiguratorInZone: 'builtin-agent-configurator-zone',
    AgentConfiguratorInApp: 'builtin-agent-configurator-app',
    PlatformAdmin: 'builtin-platform-admin',
  } as const satisfies Record<string, Role.Id>

  const TENANT = `atn:tenant_${scope.Variable.placeholder('tenant')}`
  const ZONE = `${TENANT}:zone_${scope.Variable.placeholder('zone')}`
  const APP = `${ZONE}:app_${scope.Variable.placeholder('app')}`
  const AGENT = `agent_${scope.Variable.placeholder('agent')}`

  const OWNED: Statement.Conditions = {
    resourceOwner: { equals: '${principal.id}' },
  }

  function allow(
    actions: Statement['actions'],
    resources: readonly string[],
    conditions?: Statement.Conditions,
  ): Statement.Templated {
    return {
      actions,
      effect: 'allow',
      resources: resources as readonly Urn.Template[],
      ...(conditions && { conditions }),
    }
  }

  function agentConfigurator(
    id: Role.Id,
    level: string,
    at: string,
  ): BuiltInRole {
    return {
      description: `Change the settings of an agent granted ${level}`,
      id,
      name: `Agent Configurator (${level})`,
      statements: [allow(['admin:agent:configure'], [`${at}:${AGENT}`])],
    }
  }

  export const ALL: readonly BuiltInRole[] = [
    {
      description: 'Open the Admin Portal and list everything in the tenant',
      id: Id.TenantViewer,
      name: 'Tenant Viewer',
      statements: [
        allow(
          [
            'admin:portal:access',
            'admin:tenant:read',
            'admin:zone:read',
            'admin:app:read',
            'admin:scenario:read',
          ],
          [`${TENANT}:*`],
        ),
      ],
    },
    {
      description: "Create the tenant's users and manage their access",
      id: Id.TenantUserManager,
      name: 'Tenant User Manager',
      statements: [
        allow(
          [
            'admin:user:read',
            'admin:user:create',
            'admin:policy:read',
            'admin:policy:write',
            'admin:policy:delete',
            'admin:role:assign',
          ],
          [`${TENANT}:*`],
        ),
      ],
    },
    {
      description:
        'Edit the general information of the tenant, its zones and apps',
      id: Id.TenantEditor,
      name: 'Tenant Editor',
      statements: [
        allow(
          ['admin:tenant:update', 'admin:zone:update', 'admin:app:update'],
          [`${TENANT}:*`],
        ),
      ],
    },
    {
      description:
        'Create zones, apps, pages and scenarios, and edit or delete those one created',
      id: Id.TenantCreator,
      name: 'Tenant Creator',
      statements: [
        allow(
          [
            'admin:zone:create',
            'admin:app:create',
            'admin:page:create',
            'admin:scenario:create',
          ],
          [`${TENANT}:*`],
        ),
        allow(
          [
            'admin:zone:update',
            'admin:zone:delete',
            'admin:app:update',
            'admin:app:delete',
            'admin:page:update',
            'admin:page:delete',
            'admin:scenario:update',
            'admin:scenario:delete',
          ],
          [`${TENANT}:*`],
          OWNED,
        ),
      ],
    },
    {
      description: 'Delete any zone, app, page or scenario in the tenant',
      id: Id.TenantDeleter,
      name: 'Tenant Deleter',
      statements: [
        allow(
          [
            'admin:zone:delete',
            'admin:app:delete',
            'admin:page:delete',
            'admin:scenario:delete',
          ],
          [`${TENANT}:*`],
        ),
      ],
    },
    {
      description: 'Create API keys in every zone of the tenant',
      id: Id.ApiKeyIssuerInTenant,
      name: 'API Key Issuer (tenant)',
      statements: [allow(['admin:api-key:create'], [`${TENANT}:*`])],
    },
    {
      description: 'Create API keys in the zone',
      id: Id.ApiKeyIssuerInZone,
      name: 'API Key Issuer (zone)',
      statements: [allow(['admin:api-key:create'], [ZONE])],
    },
    {
      description:
        'Create scenarios in the app, and edit or delete those one created',
      id: Id.ScenarioCreator,
      name: 'Scenario Creator',
      statements: [
        allow(['admin:scenario:create'], [APP]),
        allow(
          ['admin:scenario:update', 'admin:scenario:delete'],
          [`${APP}:*`],
          OWNED,
        ),
      ],
    },
    {
      description: 'Delete any scenario in the app',
      id: Id.ScenarioDeleter,
      name: 'Scenario Deleter',
      statements: [allow(['admin:scenario:delete'], [`${APP}:*`])],
    },
    {
      description: "Read and change the app's functionality configuration",
      id: Id.AppConfigurator,
      name: 'App Configurator',
      statements: [allow(['admin:app:configure'], [APP])],
    },
    agentConfigurator(Id.AgentConfiguratorInTenant, 'tenant-wide', TENANT),
    agentConfigurator(Id.AgentConfiguratorInZone, 'for a zone', ZONE),
    agentConfigurator(Id.AgentConfiguratorInApp, 'for an app', APP),
    {
      description: 'Everything, everywhere',
      id: Id.PlatformAdmin,
      name: 'Platform Admin',
      statements: [allow(['*'], ['*'])],
    },
  ]

  /** The stored {@link Role} for a built-in role */
  export function toRole(
    role: BuiltInRole,
    at: number,
  ): Omit<Role, 'createdBy' | 'updatedBy'> {
    return {
      ...role,
      createdAt: at,
      scopeContract: scope.contractOf(role.statements),
      tenantId: null,
      updatedAt: at,
    }
  }
}
